import React, { useContext, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { createBrowserHistory } from "history";
import { Box, CircularProgress, Typography } from "@mui/material";
import ProtectedRoute from "./ProtectedRoute.jsx";
import { AuthContext } from "context/Authentication";
import ScrollToTop from "./ScrollToTop.jsx";
import { CheckRouteAccess } from "commonmethods/Authorization";

// 🏠 CRITICAL PUBLIC ROUTES - Load Immediately (SEO + Core UX)
import { default as Home } from "./views/Home/Home.jsx";
import Login from "templates/default/views/Auth/Login.jsx";
import Register from "templates/default/views/Auth/Register.jsx";
import ProductList from "templates/default/views/ProductList/ProductList.jsx";
import ProductView from "templates/default/views/ProductView/ProductView.jsx";
import Policy from "Policy/Policy.jsx";

// 🚀 SMART LAZY COMPONENTS - Webpack-Friendly Static Imports
// 👤 Customer Features (Level 1 Priority)
const UserAccount = lazy(() => import("Users/UserAccount/UserAccount.jsx"));
const Wallet = lazy(() => import("Users/Wallet/Wallet.jsx"));
const MyOrders = lazy(() => import("OrderManagement/MyOrders/MyOrders.jsx"));
const MyBookings = lazy(() =>
  import("BookingManagement/MyBookings/MyBookings.jsx")
);
const Offers = lazy(() => import("OrderManagement/Offers/Offers.jsx"));
const Coupons = lazy(() => import("OrderManagement/Coupons/Coupons.jsx"));
const VendorProfileRoutes = lazy(() =>
  import("CMSRoutes/VendorProfileRoutes.jsx")
);
const UserUploads = lazy(() =>
  import("BookingManagement/UserUploads/UserUploads.jsx")
);
const MySubscriptions = lazy(() =>
  import("templates/default/views/Subscriptions/MySubscriptions.jsx")
);
const AddToCart = lazy(() => import("./views/AddToCart/AddToCart.jsx"));
const AfterProceedToCheckout = lazy(() =>
  import("./views/AfterProceedToCheckout/AfterProceedToCheckout.jsx")
);
const GuestCheckout = lazy(() =>
  import("./views/GuestCheckout/GuestCheckout.jsx")
);
const GuestOrderSuccess = lazy(() =>
  import("./views/GuestOrderSuccess/GuestOrderSuccess.jsx")
);

// ⚙️ Admin/CMS Features (Level 2 Priority)
const ManageContentRoutes = lazy(() =>
  import("CMSRoutes/ManageContentRoutes.jsx")
);
const WikiRoutes = lazy(() => import("CMSRoutes/WikiRoutes.jsx"));
const UsersRoutes = lazy(() => import("CMSRoutes/UsersRoutes.jsx"));
const UserTypeRoutes = lazy(() => import("CMSRoutes/UserTypesRoutes.jsx"));
const PermissionRoutes = lazy(() => import("CMSRoutes/PermissionRoutes.jsx"));
const PagesRoutes = lazy(() => import("CMSRoutes/PagesRoutes.jsx"));
const SectionContentRoutes = lazy(() =>
  import("CMSRoutes/SectionContentRoutes.jsx")
);
const ManageFAQsRoutes = lazy(() => import("CMSRoutes/ManageFAQsRoutes.jsx"));
const SectionMasterRoutes = lazy(() =>
  import("CMSRoutes/SectionMasterRoutes.jsx")
);
const ComponentMasterRoutes = lazy(() =>
  import("CMSRoutes/ComponentMasterRoutes.jsx")
);
const BannerSliderRoutes = lazy(() =>
  import("CMSRoutes/BannerSlidersRoutes.jsx")
);
const PageSectionRoutes = lazy(() => import("CMSRoutes/PageSectionRoutes.jsx"));
const SectionComponentRoutes = lazy(() =>
  import("CMSRoutes/SectionComponentRoutes.jsx")
);
const CompanyInfoRoutes = lazy(() => import("CMSRoutes/CompanyInfoRoutes.jsx"));
const CompanyInfoForm = lazy(() =>
  import("../../CompanyInfo/CompanyInfoForm.jsx")
);
const AttributeMasterRoutes = lazy(() =>
  import("CMSRoutes/AttributeMasterRoutes.jsx")
);
const BrandRoutes = lazy(() => import("CMSRoutes/BrandRoutes.jsx"));
const ProductCategoriesRoutes = lazy(() =>
  import("CMSRoutes/ProductCategoriesRoutes.jsx")
);
const ProductSubcategoriesRoutes = lazy(() =>
  import("CMSRoutes/ProductSubcategoriesRoutes.jsx")
);
const ProductRoutes = lazy(() => import("CMSRoutes/ProductRoutes.jsx"));
const DashboardRoutes = lazy(() => import("CMSRoutes/DashboardRoutes.jsx"));
const LocationRoutes = lazy(() => import("CMSRoutes/LocationRoutes.jsx"));
const OutletRoutes = lazy(() => import("CMSRoutes/OutletRoutes.jsx"));
const SupplierRoutes = lazy(() => import("CMSRoutes/SupplierRoutes.jsx"));
const MyAddressesRoutes = lazy(() => import("CMSRoutes/MyAddressesRoutes.jsx"));
const BeneficiariesRoute = lazy(() =>
  import("CMSRoutes/BeneficiariesRoute.jsx")
);
const AppSettingsRoutes = lazy(() => import("CMSRoutes/AppSettingsRoutes.jsx"));
const FoodMenusRoutes = lazy(() => import("CMSRoutes/FoodMenusRoutes.jsx"));
const SchoolZoneRoutes = lazy(() => import("CMSRoutes/SchoolZoneRoutes.jsx"));
const BannersRoutes = lazy(() => import("CMSRoutes/BannersRoutes.jsx"));
const ServicesRoutes = lazy(() => import("CMSRoutes/ServicesRoutes.jsx"));
const ManageCouponsRoute = lazy(() =>
  import("CMSRoutes/ManageCouponsRoutes.jsx")
);
const ManageOffersRoutes = lazy(() =>
  import("CMSRoutes/ManageOffersRoutes.jsx")
);
const PolicyDocumentsRoutes = lazy(() =>
  import("CMSRoutes/PolicyDocumentsRoutes.jsx")
);
const ManageOrdersRoutes = lazy(() =>
  import("CMSRoutes/ManageOrdersRoutes.jsx")
);
const VendorsRoutes = lazy(() => import("CMSRoutes/VendorsRoutes.jsx"));
const ManageBookings = lazy(() =>
  import("BookingManagement/ManageBookings/ManageBookings.jsx")
);
const SubscriptionPlansRoutes = lazy(() =>
  import("CMSRoutes/SubscriptionPlansRoutes.jsx")
);
const SupportTicketsRoutes = lazy(() =>
  import("CMSRoutes/SupportTicketsRoutes.jsx")
);
const ManageSubscriptions = lazy(() =>
  import("templates/default/views/Subscriptions/ManageSubscriptions.jsx")
);
const OrderSummaryReportRoutes = lazy(() =>
  import("CMSRoutes/OrderSummaryReportRoutes.jsx")
);
const ManageBlogRoutes = lazy(() => import("CMSRoutes/ManageBlogRoutes.jsx"));
const Blogs = lazy(() => import("templates/default/views/Blogs/Blogs.jsx"));
const BlogView = lazy(() => import("templates/default/views/Blogs/BlogView.jsx"));

// 🔧 Utilities (Level 3 Priority)
const ServiceList = lazy(() =>
  import("templates/default/views/ServiceList/ServiceList.jsx")
);
const ServiceView = lazy(() =>
  import("templates/default/views/ServicePreview/ServiceView.jsx")
);
const ServicePreview = lazy(() =>
  import("./views/ServicePreview/ServicePreview.jsx")
);
const VendorPreview = lazy(() =>
  import("templates/default/views/VendorPreview/VendorPreview.jsx")
);
const AudioPlayer = lazy(() => import("AudioPlayer/AudioPlayer.jsx"));
const ResetPassword = lazy(() =>
  import("templates/default/views/Auth/ResetPassword.jsx")
);
const PaymentCallback = lazy(() =>
  import("templates/default/views/AfterProceedToCheckout/PaymentCallback.jsx")
);
const RetryPayment = lazy(() =>
  import("OrderManagement/RetryPayment/RetryPayment.jsx")
);
const SubscriptionList = lazy(() =>
  import("templates/default/views/Subscriptions/SubscriptionList.jsx")
);
const ServicesWithVendors = lazy(() =>
  import("templates/default/views/ServicePreview/ServicesWithVendors.jsx")
);
const About = lazy(() => import("templates/default/views/About/About.jsx"));
const SchoolMenuLanding = lazy(() =>
  import("ProductManagement/FoodMenus/SchoolMenuLanding.jsx")
);
const Faqs = lazy(() => import("templates/default/views/Faqs/Faqs.jsx"));

// 🎨 SMART LOADING COMPONENTS
const CustomerLoader = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="300px"
  >
    <CircularProgress size={32} color="primary" />
    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
      Loading your account...
    </Typography>
  </Box>
);

const AdminLoader = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="400px"
  >
    <CircularProgress size={40} color="secondary" />
    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
      Loading admin panel...
    </Typography>
  </Box>
);

const UtilityLoader = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
  >
    <CircularProgress size={28} />
    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
      Loading...
    </Typography>
  </Box>
);

var hist = createBrowserHistory();

export default function DefaultRoutes() {
  const context = useContext(AuthContext);

  const checkAccess = (path) => {
    const routeAccess = CheckRouteAccess(path);
    return routeAccess ? routeAccess.Can_View : false;
  };

  // Check if About page is enabled
  const aboutEnabledSetting = context.state.appSettings?.find(
    (setting) => setting.Setting_Name === "About Enabled"
  );
  const isAboutEnabled = aboutEnabledSetting?.Setting_Value === "Yes";

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <Routes>
        {/* 🛡️ PROTECTED ROUTES - Smart Authentication Wrapper */}
        <Route
          element={
            <ProtectedRoute
              isAllowed={
                context.state &&
                context.state.isAuthenticated &&
                !!context.state.user
              }
              currentUser={
                context.state &&
                context.state.isAuthenticated &&
                context.state.user
              }
            />
          }
        >
          {/* 👤 CUSTOMER ROUTES - Level 1 Priority (Lazy Loaded) */}
          <Route
            exact
            path="/Account"
            element={
              checkAccess("/Account") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <UserAccount />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Wallet"
            element={
              checkAccess("/Wallet") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <Wallet />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/MyOrders"
            element={
              checkAccess("/MyOrders") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <MyOrders />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/MyBookings"
            element={
              <Suspense fallback={<CustomerLoader />}>
                <MyBookings />
              </Suspense>
            }
          />
          <Route
            exact
            path="/Offers"
            element={
              checkAccess("/Offers") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <Offers />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Coupons"
            element={
              checkAccess("/Coupons") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <Coupons />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/VendorProfile"
            element={
              <Suspense fallback={<CustomerLoader />}>
                <VendorProfileRoutes />
              </Suspense>
            }
          />
          <Route
            exact
            path="/UserUploads"
            element={
              checkAccess("/UserUploads") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <UserUploads />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/MySubscriptions"
            element={
              checkAccess("/MySubscriptions") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <MySubscriptions />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/MyAddresses"
            element={
              checkAccess("/MyAddresses") ? (
                <Suspense fallback={<CustomerLoader />}>
                  <MyAddressesRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />

          {/* ⚙️ ADMIN/CMS ROUTES - Level 2 Priority (Lazy Loaded) */}
          <Route
            exact
            path="/ManageContent"
            element={
              checkAccess("/ManageContent") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageContentRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Wiki"
            element={
              checkAccess("/Wiki") ? (
                <Suspense fallback={<AdminLoader />}>
                  <WikiRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Users"
            element={
              checkAccess("/Users") ? (
                <Suspense fallback={<AdminLoader />}>
                  <UsersRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/UserTypes"
            element={
              checkAccess("/UserTypes") ? (
                <Suspense fallback={<AdminLoader />}>
                  <UserTypeRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Permissions"
            element={
              checkAccess("/Permissions") ? (
                <Suspense fallback={<AdminLoader />}>
                  <PermissionRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SectionMaster"
            element={
              checkAccess("/SectionMaster") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SectionMasterRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ComponentMaster"
            element={
              checkAccess("/ComponentMaster") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ComponentMasterRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/PageSections"
            element={
              checkAccess("/PageSections") ? (
                <Suspense fallback={<AdminLoader />}>
                  <PageSectionRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SectionComponents"
            element={
              checkAccess("/SectionComponents") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SectionComponentRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Pages"
            element={
              checkAccess("/Pages") ? (
                <Suspense fallback={<AdminLoader />}>
                  <PagesRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SectionContent"
            element={
              checkAccess("/SectionContent") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SectionContentRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageFAQs"
            element={
              checkAccess("/ManageFAQs") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageFAQsRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/BannerSlider"
            element={
              checkAccess("/BannerSlider") ? (
                <Suspense fallback={<AdminLoader />}>
                  <BannerSliderRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/PageSection"
            element={
              checkAccess("/PageSection") ? (
                <Suspense fallback={<AdminLoader />}>
                  <PageSectionRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SectionComponents"
            element={
              checkAccess("/SectionComponents") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SectionComponentRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/CompanyInfo"
            element={
              checkAccess("/CompanyInfo") ? (
                <Suspense fallback={<AdminLoader />}>
                  <CompanyInfoRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/CompanyInfoForm"
            element={
              checkAccess("/CompanyInfoForm") ? (
                <Suspense fallback={<AdminLoader />}>
                  <CompanyInfoForm />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/AttributeMaster"
            element={
              checkAccess("/AttributeMaster") ? (
                <Suspense fallback={<AdminLoader />}>
                  <AttributeMasterRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Brands"
            element={
              checkAccess("/Brands") ? (
                <Suspense fallback={<AdminLoader />}>
                  <BrandRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ProductCategories"
            element={
              checkAccess("/ProductCategories") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ProductCategoriesRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ProductSubcategories"
            element={
              checkAccess("/ProductSubcategories") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ProductSubcategoriesRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Products"
            element={
              checkAccess("/Products") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ProductRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/AdminDashboard"
            element={
              checkAccess("/AdminDashboard") ? (
                <Suspense fallback={<AdminLoader />}>
                  <DashboardRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Locations"
            element={
              checkAccess("/Locations") ? (
                <Suspense fallback={<AdminLoader />}>
                  <LocationRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Outlets"
            element={
              checkAccess("/Outlets") ? (
                <Suspense fallback={<AdminLoader />}>
                  <OutletRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Suppliers"
            element={
              checkAccess("/Suppliers") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SupplierRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Beneficiaries"
            element={
              checkAccess("/Beneficiaries") ? (
                <Suspense fallback={<AdminLoader />}>
                  <BeneficiariesRoute />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/AppSettings"
            element={
              checkAccess("/AppSettings") ? (
                <Suspense fallback={<AdminLoader />}>
                  <AppSettingsRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/FoodMenus"
            element={
              checkAccess("/FoodMenus") ? (
                <Suspense fallback={<AdminLoader />}>
                  <FoodMenusRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SchoolMenu"
            element={
              checkAccess("/SchoolMenu") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SchoolZoneRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Banners"
            element={
              checkAccess("/Banners") ? (
                <Suspense fallback={<AdminLoader />}>
                  <BannersRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Services"
            element={
              checkAccess("/Services") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ServicesRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageCoupons"
            element={
              checkAccess("/ManageCoupons") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageCouponsRoute />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageOffers"
            element={
              checkAccess("/ManageOffers") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageOffersRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/Policies"
            element={
              checkAccess("/Policies") ? (
                <Suspense fallback={<AdminLoader />}>
                  <PolicyDocumentsRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageOrders"
            element={
              checkAccess("/ManageOrders") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageOrdersRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageVendors"
            element={
              checkAccess("/ManageVendors") ? (
                <Suspense fallback={<AdminLoader />}>
                  <VendorsRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageBookings"
            element={
              checkAccess("/ManageBookings") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageBookings />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SubscriptionPlans"
            element={
              checkAccess("/SubscriptionPlans") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SubscriptionPlansRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/SupportTickets"
            element={
              checkAccess("/SupportTickets") ? (
                <Suspense fallback={<AdminLoader />}>
                  <SupportTicketsRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageSubscriptions"
            element={
              checkAccess("/ManageSubscriptions") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageSubscriptions />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/OrderSummaryReport"
            element={
              checkAccess("/OrderSummaryReport") ? (
                <Suspense fallback={<AdminLoader />}>
                  <OrderSummaryReportRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            exact
            path="/ManageBlogs"
            element={
              checkAccess("/ManageBlogs") ? (
                <Suspense fallback={<AdminLoader />}>
                  <ManageBlogRoutes />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Route>

        {/* 🏠 CRITICAL PUBLIC ROUTES - Immediate Loading (No Lazy Loading) */}
        <Route exact path="/" element={<Home />} />
        <Route exact path="/Login" element={<Login />} />
        <Route exact path="/Register" element={<Register />} />
        <Route
          exact
          path="/ProductList/:category?/:subcategory?/:tags?"
          element={<ProductList />}
        />
        <Route
          exact
          path="/ProductView/:productName/:combinationText?/:combinationId?"
          element={<ProductView />}
        />
        <Route exact path="/Policy/:title" element={<Policy />} />
        <Route
          exact
          path="/Faqs"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <Faqs />
            </Suspense>
          }
        />
        <Route
          exact
          path="/Blogs"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <Blogs />
            </Suspense>
          }
        />
        <Route
          exact
          path="/blog/:id"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <BlogView />
            </Suspense>
          }
        />

        {/* 🔧 UTILITY ROUTES - Level 3 Priority (Lazy Loaded) */}
        <Route
          exact
          path="/AddToCart"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <AddToCart />
            </Suspense>
          }
        />
        <Route
          exact
          path="/AfterProceedToCheckout"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <AfterProceedToCheckout />
            </Suspense>
          }
        />
        <Route
          exact
          path="/GuestCheckout"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <GuestCheckout />
            </Suspense>
          }
        />
        <Route
          exact
          path="/GuestOrderSuccess/:orderCode"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <GuestOrderSuccess />
            </Suspense>
          }
        />
        <Route
          exact
          path="/ServiceList/:category?/:subcategory?"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <ServiceList />
            </Suspense>
          }
        />
        <Route
          exact
          path="/ServiceView/:serviceId"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <ServiceView />
            </Suspense>
          }
        />
        <Route
          exact
          path="/service/:serviceId"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <ServicePreview />
            </Suspense>
          }
        />
        <Route
          exact
          path="/VendorPreview/:vendorId"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <VendorPreview />
            </Suspense>
          }
        />
        <Route
          exact
          path="/AudioPlayer/:serviceId"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <AudioPlayer />
            </Suspense>
          }
        />
        <Route
          exact
          path="/ResetPassword/:passKey"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <ResetPassword />
            </Suspense>
          }
        />
        <Route
          exact
          path="/payment-callback"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <PaymentCallback />
            </Suspense>
          }
        />
        <Route
          exact
          path="/retry-payment/:orderId"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <RetryPayment />
            </Suspense>
          }
        />
        <Route
          exact
          path="/SubscriptionList"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <SubscriptionList />
            </Suspense>
          }
        />
        <Route
          exact
          path="/ServicesWithVendors/:shortname"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <ServicesWithVendors />
            </Suspense>
          }
        />
        {isAboutEnabled && (
          <Route
            exact
            path="/About"
            element={
              <Suspense fallback={<UtilityLoader />}>
                <About />
              </Suspense>
            }
          />
        )}
        <Route
          exact
          path="/SchoolMenuLanding"
          element={
            <Suspense fallback={<UtilityLoader />}>
              <SchoolMenuLanding />
            </Suspense>
          }
        />
      </Routes>
    </Router>
  );
}
